import { Link } from "react-router-dom"

import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useBatteryProposalArbitrage from "~/api/hooks/useBatteryProposalArbitrage"
import {
  useBatterySavingsReport,
  useDownloadBatterySavingsReport,
} from "~/api/hooks/useBatterySavingsReport"
import useRetryBatteryProposalArbitrage from "~/api/hooks/useRetryBatteryProposalArbitrage"
import useSetBatterySavingsVisibility from "~/api/hooks/useSetBatterySavingsVisibility"

import { batteryArbitrageStatusView } from "./battery-arbitrage-status"
import { batterySetupStepPath, SETUP_STEP_LABELS } from "./setup-steps"

export const DOWNLOAD_SAVINGS_REPORT_LABEL = `Download Battery Savings Report`

/** Both seller entry points read the same server-owned eligibility contract. */
export function BatterySavingsReportAction({
  proposalId,
}: {
  proposalId: string
}): JSX.Element {
  const canUpdate = useBatteryProposal(proposalId).data?.viewerCanUpdate === true
  const availability = useBatterySavingsReport(proposalId)
  const download = useDownloadBatterySavingsReport(proposalId)
  const show = useSetBatterySavingsVisibility()
  const calculate = useRetryBatteryProposalArbitrage()
  const state = availability.data
  const blocked = state && !state.ready ? state : null
  const arbitrage = useBatteryProposalArbitrage(proposalId, {
    enabled: blocked?.recovery === `complete_inputs`,
  })
  const fixStep = arbitrage.data
    ? batteryArbitrageStatusView(arbitrage.data).fixStep
    : null
  const busy = show.isLoading || calculate.isLoading || download.isLoading
  const controlClass = `rounded-md border border-control-border px-3 py-2 text-sm font-semibold text-foreground disabled:cursor-not-allowed disabled:opacity-50`

  const startDownload = (): void => {
    void download
      .mutateAsync()
      .then(({ pdf, filename }) => {
        const url = URL.createObjectURL(pdf)
        const link = document.createElement(`a`)
        link.href = url
        link.download = filename
        document.body.append(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 60_000)
      })
      .catch(() => {
        /* The mutation error is rendered below. */
      })
  }

  return (
    <section
      aria-label="Battery Savings Report"
      className="my-4 flex flex-col items-start gap-3 rounded-lg border border-border bg-surface p-4"
    >
      <h3 className="font-display text-base font-semibold text-foreground">
        Battery Savings Report
      </h3>
      <p className="text-sm text-muted">
        A standalone explanation of the saved electricity savings estimate, its
        monthly results and assumptions.
      </p>
      <button
        type="button"
        disabled={!state?.ready || availability.isError || busy}
        onClick={startDownload}
        className={controlClass}
      >
        {download.isLoading
          ? `Generating Battery Savings Report`
          : DOWNLOAD_SAVINGS_REPORT_LABEL}
      </button>
      {availability.isLoading ? (
        <p role="status" className="text-sm text-muted">
          Checking report availability
        </p>
      ) : null}
      {availability.isError ? (
        <>
          <p role="alert" className="text-sm text-danger">
            Unable to check report availability.
          </p>
          <button
            type="button"
            className={controlClass}
            onClick={() => void availability.refetch()}
          >
            Retry availability check
          </button>
        </>
      ) : blocked ? (
        <>
          <p role="status" className="text-sm text-muted">
            {blocked.message}
          </p>
          {canUpdate && blocked.recovery === `show` ? (
            <button
              type="button"
              disabled={busy}
              className={controlClass}
              onClick={() => show.mutate({ proposalId, visibility: `shown` })}
            >
              Show savings and calculate
            </button>
          ) : null}
          {canUpdate &&
          (blocked.recovery === `recalculate` ||
            blocked.recovery === `retry`) ? (
            <button
              type="button"
              disabled={busy}
              className={controlClass}
              onClick={() => calculate.mutate({ proposalId })}
            >
              {blocked.recovery === `retry`
                ? `Retry calculation`
                : `Recalculate savings`}
            </button>
          ) : null}
          {canUpdate && blocked.recovery === `complete_inputs` ? (
            <Link
              className="text-sm font-semibold text-primary underline"
              to={batterySetupStepPath(proposalId, fixStep ?? `arbitrage`)}
            >
              Open {SETUP_STEP_LABELS[fixStep ?? `arbitrage`]}
            </Link>
          ) : null}
        </>
      ) : null}
      {blocked?.recovery && !canUpdate ? (
        <p className="text-sm text-muted">
          You have view-only access. Ask a seller with editing access to update
          Battery Savings.
        </p>
      ) : null}
      {show.isError || calculate.isError ? (
        <p role="alert" className="text-sm text-danger">
          Unable to start the calculation. Try the action again.
        </p>
      ) : null}
      {download.isError && state?.ready ? (
        <>
          <p role="alert" className="text-sm text-danger">
            Unable to generate the Battery Savings Report. Try downloading again.
          </p>
          <button
            type="button"
            className={controlClass}
            disabled={busy}
            onClick={startDownload}
          >
            Retry download
          </button>
        </>
      ) : null}
      {download.isSuccess ? (
        <p role="status" className="text-sm text-muted">
          Battery Savings Report downloaded.
        </p>
      ) : null}
    </section>
  )
}
